<template>
  <div v-if="dynamicForm">
    <vx-card no-shadow>
      <vs-breadcrumb class="mt-2">
        <li>
          <router-link to="/crm/dynamicForms"> Dynamic forms</router-link
          ><span class="vs-breadcrum--separator">/</span>
        </li>
        <li aria-current="page" class="active">Edit form</li>
      </vs-breadcrumb>

      <div class="vx-row mb-2 mt-2">
        <div class="vx-col sm:w-2/5 w-full">
          <h2>Dynamic form</h2>
        </div>
      </div>

      <div class="vx-col sm:w-3/5 w-full mb-8">
        <p class="mb-0">
          A dynamic form will be shown on Rekom websites. This is the dynamic form template. The logo,
          background-, text-, button background- and button text-color will automatically be adjusted to match
          the individual design of a venue.
          <br />
        </p>
        <p class="b-5">
          You can specify dynamic content by using the <b>Merge Tags</b> feature in the form editor.<br />
        </p>
      </div>

      <div class="vx-row">
        <div class="vx-col sm:w-3/5">
          <div class="vx-row mb-6">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Active</span>
            </div>
            <div class="vx-col">
              <vs-switch color="success" v-model="dynamicForm.enabled" />
            </div>
          </div>

          <div class="vx-row mb-6">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Title</span>
            </div>
            <div class="vx-col sm:w-4/5 w-full">
              <vs-input label="" v-model="dynamicForm.title" class="w-full" />
            </div>
          </div>

          <div class="vx-row mb-6">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Category</span>
            </div>
            <div class="vx-col sm:w-4/5 w-full">
              <vs-input label="" v-model="dynamicForm.category" class="w-full" />
            </div>
          </div>

          <div class="vx-row mb-6">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Label(s) to add to user after submit</span>
            </div>
            <div class="vx-col sm:w-4/5 w-full">
              <vs-select class="w-full" multiple v-model="dynamicForm.labels">
                <vs-select-item :key="item" :value="item" :text="item" v-for="item in labels" />
              </vs-select>
            </div>
          </div>

          <div class="vx-row mb-6">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Open type</span>
            </div>
            <div class="vx-col sm:w-4/5 w-full">
              <vs-select class="w-full" v-model="dynamicForm.openType">
                <vs-select-item
                  :key="item"
                  :value="item"
                  :text="openTypeToLabel(item)"
                  v-for="item in formOpenTypes"
                />
              </vs-select>
            </div>
          </div>

          <div class="vx-row mb-6" v-if="dynamicForm.openType == 'Automatic'">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Venues</span>
            </div>
            <div class="vx-col sm:w-4/5 w-full">
              <venue-selector v-model="dynamicForm.venues" :multiple="true" />
            </div>
          </div>

          <div class="vx-row mb-6" v-if="dynamicForm.openType == 'Automatic'">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Time constrained</span>
            </div>
            <div class="vx-col">
              <vs-switch color="success" v-model="dynamicForm.isTimeConstrained" />
            </div>
          </div>

          <div class="vx-row mb-6" v-if="dynamicForm.isTimeConstrained">
            <div class="vx-col sm:w-1/5 w-full">
              <span>Start date</span>
            </div>
            <div class="vx-col">
              <DatePicker
                :attributes="calendarAttributes"
                is-expanded
                v-model="dynamicForm.startDate"
              ></DatePicker>
            </div>
          </div>

          <div class="vx-row mb-6" v-if="dynamicForm.isTimeConstrained">
            <div class="vx-col sm:w-1/5 w-full">
              <span>End date</span>
            </div>
            <div class="vx-col">
              <DatePicker
                :attributes="calendarAttributes"
                is-expanded
                v-model="dynamicForm.endDate"
              ></DatePicker>
            </div>
          </div>
        </div>
        <div class="vx-col sm:w-2/5">
          <vs-row vs-justify="flex-end">
            <vx-card class="max-w-sm">
              <b>Implementation code</b><br />
              <span>Select how the form should be loaded</span>
              <vs-row class="mt-3" vs-justify="center">
                <vs-button
                  @click="
                    codePopupText = previewCodes.button;
                    codePopupActive = true;
                  "
                  class="w-full mb-2"
                  >Form button</vs-button
                >
                <vs-button
                  @click="
                    codePopupText = previewCodes.link;
                    codePopupActive = true;
                  "
                  class="w-full mb-2"
                  >Form link</vs-button
                >
                <vs-button
                  @click="
                    codePopupText = previewCodes.inline;
                    codePopupActive = true;
                  "
                  class="w-full"
                  >Inline form</vs-button
                >
              </vs-row>
            </vx-card>
          </vs-row>
        </div>
      </div>

      <vs-row v-if="!isLoading">
        <vs-col>
          <form-editor
            :legal-documents="legalDocuments"
            :interests="interests"
            :formJson="dynamicForm.formJson"
            ref="formEditor"
          />
        </vs-col>
        <vs-row class="top-editor-wrapper py-2 px-4" vs-align="center">
          <vs-button @click="previewForVenue" class="px-4" type="border">Preview</vs-button>
          <span class="mx-2">for venue</span>

          <vs-select v-model="previewVenue">
            <vs-select-item
              :key="item.value"
              :value="item.value"
              :text="item.text"
              v-for="item in selectablePreviewVenues"
            />
          </vs-select>
        </vs-row>
      </vs-row>

      <!-- Save & Remove Button -->
      <vs-row vs-justify="space-between" class="flex flex-wrap items-center" style="margin-top: 32px">
        <vs-button
          v-if="$AuthService.hasPermission('vp.crm.dynamicforms.edit')"
          id="remove-button"
          @click="confirmRemove"
          color="danger"
          class="mt-2"
          >Remove</vs-button
        >
        <div>
          <vs-button
            v-if="$AuthService.hasPermission('vp.crm.dynamicforms.edit')"
            id="save-button"
            @click="update"
            class="mt-2 mr-2"
            >Save Changes</vs-button
          >
          <vs-button
            v-if="$AuthService.hasPermission('vp.crm.dynamicforms.edit')"
            id="push-button"
            @click="confirmExport"
            class="mt-2"
            >Push to websites</vs-button
          >
        </div>
      </vs-row>
    </vx-card>

    <vs-popup v-if="codePopupActive" :active.sync="codePopupActive" title="Embed code">
      <p class="pb-2">Copy and paste the following code to embed the form</p>
      <vs-textarea readonly v-model="codePopupText"></vs-textarea>
    </vs-popup>

    <vs-popup fullscreen :active.sync="previewPopupActive" title="Preview" id="previewPopup">
      <iframe
        v-if="previewPopupActive"
        width="100%"
        height="95%"
        style="border: none"
        :srcdoc="iframeCode"
      ></iframe>
    </vs-popup>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import { DatePicker } from "v-calendar";
import FormEditor from "@/components/FormEditor/FormEditor.vue";
import VenueSelector from "@/components/VenueSelector.vue";

export default {
  components: { DatePicker, FormEditor, VenueSelector },
  props: {},
  data() {
    return {
      codePopupActive: false,
      codePopupText: "",

      previewVenue: "",
      previewHtml: "",
      previewPopupActive: false,

      isLoading: true,
      momentFormat: "DD-MM-YYYY HH:mm", // "DD-MM-YYYY HH:mm" for time
      calendarAttributes: [
        {
          key: "today",
          highlight: "gray",
          dates: new Date(),
        },
      ],
      dynamicFormId: "",
    };
  },
  methods: {
    openTypeToLabel(type) {
      switch (type) {
        case "Automatic":
          return "Automatic popup";
        case "Manual":
          return "Manual";
      }
    },
    removeTag(item, tags) {
      tags.splice(tags.indexOf(item), 1);
    },
    async update() {
      this.$vs.loading();

      try {
        const formExport = await this.$refs.formEditor.export();
        this.dynamicForm.formJson = JSON.stringify(formExport.design);
        // this.dynamicForm.formHtml = formExport.html;

        await this.$store.dispatch("dynamicForms/update", this.dynamicForm);

        this.$vs.notify({
          color: "green",
          title: "Dynamic form saved",
          text: "The Dynamic form was successfully saved",
        });
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Dynamic form not saved",
          text: "Dynamic form could not be saved",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async confirmRemove() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm`,
        text: "Are you sure you want to delete this dynamic form?",
        accept: this.remove,
      });
    },
    async remove() {
      this.$vs.loading();
      await this.$store.dispatch("dynamicForms/delete", this.dynamicFormId);
      this.$vs.loading.close();
      this.$router.go(-1);
    },
    async confirmExport() {
      this.$vs.dialog({
        type: "confirm",
        color: "primary",
        title: `Confirm`,
        text: "Are you sure you want to push this form to the live websites? It will be immediately visible to visitors.",
        accept: async () => {
          this.$vs.loading({ text: "Pushing form to websites..." });

          try {
            const formExport = await this.$refs.formEditor.export();
            this.dynamicForm.formJson = JSON.stringify(formExport.design);

            // Also saves
            await this.$store.dispatch("dynamicForms/exportDynamicForm", this.dynamicForm);

            this.$vs.notify({
              color: "green",
              title: "Dynamic form saved",
              text: "The Dynamic form was successfully exported to all websites",
            });
          } catch (err) {
            console.error(err);

            this.$vs.notify({
              color: "danger",
              title: "Dynamic form not saved",
              text: "Dynamic form could not be exported",
            });
          } finally {
            this.$vs.loading.close();
          }
        },
      });
    },
    async previewForVenue() {
      if (this.previewVenue == "") return;
      this.$vs.loading({ text: "Loading preview..." });
      try {
        const formExport = await this.$refs.formEditor.export();
        this.previewHtml = await this.$store.dispatch("dynamicForms/previewForVenue", {
          venueId: this.previewVenue,
          formJson: JSON.stringify(formExport.design),
        });
        this.previewPopupActive = true;
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  computed: {
    ...mapGetters({
      formOpenTypes: "dynamicForms/formOpenTypes",
      venues: "venues/venues",
      legalDocuments: "legalDocuments/documents",
      interests: "guestSegmentation/interests",
    }),

    iframeCode() {
      console.log(this.previewVenue, this.dynamicFormId);
      return `
        <html>
        <head>
          <${"script"} type="text/javascript" src="https://rekomlifestorageprod.blob.core.windows.net/crm/rekom-form.js"></${"script"}>
        </head>
        <body style="display:flex; justify-content: center;">
          <rekom-form-manager venueid="${this.previewVenue}" preview-mode="true"></rekom-form-manager>
          <div rekom-inline-form>Form</div> 
          <${"script"}>
          const $formManager = document.querySelector("rekom-form-manager");
          $formManager.getVueInstance().setPreviewHtml(${JSON.stringify(this.previewHtml)});
          </${"script"}>
        </body>
        </html>
      `;
    },
    previewCodes() {
      return {
        inline: `<div rekom-inline-form form-id="${this.dynamicFormId}"></div>`,
        button: `<button onclick="rekomLoadDynamicForm('${this.dynamicFormId}')">Form button</button>`,
        link: `<a href="javascript:rekomLoadDynamicForm('${this.dynamicFormId}')">Form link</a>`,
      };
    },
    selectablePreviewVenues() {
      return (
        _.chain(this.venues)
          // All if manual, otherwise only selected venues
          .filter(e => this.dynamicForm.openType === "Manual" || this.dynamicForm.venues.includes(e.venueId))
          .map(e => ({ text: e.name, value: e.venueId }))
          .value()
      );
    },
    ...mapGetters({
      labels: "guestSegmentation/labels",
      formOpenTypes: "dynamicForms/formOpenTypes",
      venues: "venues/venues",
      legalDocuments: "legalDocuments/documents",
    }),
    dynamicForm() {
      return this.$store.getters["dynamicForms/dynamicForm"](this.dynamicFormId);
    },
  },
  async created() {
    this.isLoading = true;
    this.dynamicFormId = this.$route.params.id;
    await this.$store.dispatch("guestSegmentation/get", false);
    await this.$store.dispatch("dynamicForms/getAll", false);
    await this.$store.dispatch("legalDocuments/getAll", false);
    await this.$store.dispatch("guestSegmentation/get");
    this.isLoading = false;
  },
};
</script>

<style lang="scss">
.top-editor-wrapper {
  border: 1px solid #999;
}

#previewPopup .vs-popup--content {
  height: 100%;
}
</style>